import React, { useContext, useState } from 'react';
import { Col, Container, PopoverBody, Row, UncontrolledPopover } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ConfirmModal,
  CustomModal,
  LinkButton,
  PageHeader,
  Section,
  SpinnerArea,
  Table,
} from '../../../../../shared/components';
import OperationContext from '../../context';
import ActionPlanContext from '../ActionPlanContext';
import {
  OperActionPlanItem,
  OperationActionPlanItemRequestDto,
} from '../../../../../services/types/ActionPlan';
import Context from '../../../../App/MainProvider/context';
import operActionPlanService from '../../../../../services/operActionPlanService';
import { Cell } from '../../../../../shared/components/Table';
import { customDateStr, getErrorMessage, sortDate } from '../../../../../shared/misc/utils';
import PriorityBadge from '../../../PriorityBadge';
import ActionPlanStatusBadge from '../../ActionPlanStatusBadge';
import NewActionPlanDetailForm, { NewActionPlanDetailFormData } from './NewActionPlanDetailForm';
import ActionPlanItemContext from './ActionPlanItemContext';
import { BasicNotification } from '../../../../../shared/components/Notification';

const ActionPlanItem = () => {
  const { operation } = useContext(OperationContext);
  const { t } = useTranslation();
  const {
    operationActionPlans,
    aplicablePolicyOptions,
    indicators,
    grantedWrite,
    blokedBySR,
    stepSR,
  } = useContext(ActionPlanContext);
  const {
    operationActionPlanItems,
    operationActionPlan,
    operationActionPlanDocuments,
    loading,
    reload,
  } = useContext(ActionPlanItemContext);
  const { service, notify } = useContext(Context);
  const [showNewActionPlanDetailModal, setShowNewActionPlanDetailModal] = useState(false);

  const [showDeleteActionRowModal, setShowDeleteActionRowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<OperActionPlanItem | null>(null);

  const [saveLoading, setSaveLoading] = useState(false);
  const [width, setWidth] = React.useState(window.innerWidth);
  const [serverError, setServerError] = useState('');
  React.useEffect(() => {
    const handleWindowResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleWindowResize);

    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  const isLastVersion = operationActionPlan
    ? operationActionPlans.filter((a) => a.version > operationActionPlan.version).length === 0
    : false;
  const readOnly =
    (!!operationActionPlan && operationActionPlan.readOnly) || !grantedWrite || !isLastVersion;

  const getPolicyLocalization = (ap: OperActionPlanItem, lang: string): string => {
    if (operationActionPlan) {
      const aplicablePolicyOption = aplicablePolicyOptions.find(
        (po) =>
          po.language === lang &&
          ap.compliancePolicyId === po.policyId &&
          (!ap.complianceDirectiveId || ap.complianceDirectiveId === po.directiveId)
      );
      if (aplicablePolicyOption) {
        return (
          aplicablePolicyOption.policyTitle +
          (ap.complianceDirectiveId ? ` ${aplicablePolicyOption.directiveTitle}` : '')
        );
      }
    }
    return '';
  };

  const getIndicatorLocalization = (
    ap: OperActionPlanItem,
    lang: string,
    indicatorField: 'status' | 'priority'
  ): string => {
    const indicatorName =
      indicatorField === 'status' ? 'actionPlanStatus' : (indicatorField as string);
    if (operationActionPlan && indicators) {
      const indicator = indicators.find(
        (i) => i.name === indicatorName && i.language === lang && i.value === ap[indicatorField]
      );
      if (indicator) {
        return indicator.description;
      }
    }
    return '';
  };

  const truncateString = (value: string, chNumHigh: number, chNumLow: number): string => {
    const chNum = width > 1600 ? chNumHigh : chNumLow;
    if (!value || value.length <= chNum) {
      return value;
    }
    return `${value.substr(0, chNum - 3)}...`;
  };

  const deleteDocument = (id: number) => {
    service(() => operActionPlanService.deleteActionPlanDocument(id), {
      callback: () => {
        notify(t('Deleted document successfully'), t('successSave'), 'success');
        reload();
      },
    });
  };

  const cells: Cell<OperActionPlanItem>[] = [
    {
      name: 'dateActionRecorded',
      column: t('dateActionRecorded'),
      value: (x) => customDateStr(x.dateActionRecorded),
    },
    {
      name: 'compliancePolicyId',
      column: t('applicablePolicyOrDirective'),
      value: (x) => x.compliancePolicyId,
      render: (x, o) => {
        const text =
          o.policyDirectiveText ||
          getPolicyLocalization(o, operationActionPlan ? operationActionPlan.language : 'en');
        return <>{text}</>;
      },
    },
    {
      name: 'topic',
      column: t('topic'),
      value: (x) => truncateString(x.topic, 60, 25),
    },
    {
      name: 'action',
      column: t('action'),
      value: (x) => truncateString(x.action, 100, 25),
    },
    {
      name: 'priority',
      column: t('priority'),
      value: (x) => x.priority,
      render: (x, o) => (
        <PriorityBadge
          code={x}
          locale={getIndicatorLocalization(
            o,
            operationActionPlan ? operationActionPlan.language : 'en',
            'priority'
          )}
        />
      ),
    },
    {
      name: 'evidence',
      column: t('evidence'),
      value: (x) => truncateString(x.evidence, 25, 25),
      render: (x, o) => (
        <div className="d-flex align-items-center">
          {x}
          {o.externalDocumentLink ? (
            <span id={`doc_link_${o.id}`}>
              <LinkButton
                onClick={() => window.open(o.externalDocumentLink)}
                icon="link"
                text=""
                className="ml-2"
                title={t('evidenceDocument')}
              />
            </span>
          ) : (
            o.documentLink && (
              <span id={`doc_link_${o.id}`}>
                <LinkButton
                  onClick={() => window.open(o.documentLink)}
                  icon="file-download"
                  text=""
                  className="ml-2"
                  title={t('evidenceDocument')}
                />
              </span>
            )
          )}
          {operationActionPlanDocuments.filter((b) =>
            o.evidenceAttachmentIds.includes(b.id.toString())
          ).length > 0 &&
            (!readOnly ? (
              <>
                <LinkButton
                  htmlId={`btn_tooltip_${o.id}`}
                  icon="clipboard-list"
                  text=""
                  className="ml-2"
                  title={t('evidenceDocument')}
                />
                <UncontrolledPopover
                  placement="bottom"
                  target={`btn_tooltip_${o.id}`}
                  trigger="legacy"
                  className="popover-gantt"
                >
                  <PopoverBody>
                    <Container className="dashboard">
                      <Row>
                        <Col sm={12}>
                          <h5>Attached Documents</h5>
                        </Col>
                      </Row>
                      {operationActionPlanDocuments
                        .filter((b) => o.evidenceAttachmentIds.includes(b.id.toString()))
                        .map((d) => (
                          <Row>
                            <Col sm={9}>{d.documentName}</Col>
                            <Col sm={3} style={{ display: 'inline-flex' }}>
                              {d.documentLink && (
                                <LinkButton
                                  onClick={() => window.open(d.documentLink)}
                                  icon="file-download"
                                  text=""
                                />
                              )}
                              <LinkButton
                                onClick={() => deleteDocument(d.id)}
                                icon="trash-alt"
                                text=""
                              />
                            </Col>
                          </Row>
                        ))}
                    </Container>
                  </PopoverBody>
                </UncontrolledPopover>
              </>
            ) : (
              <>
                <LinkButton
                  htmlId={`btn_tooltip_${o.id}`}
                  icon="clipboard-list"
                  text=""
                  className="ml-2"
                  title={t('evidenceDocument')}
                />
                <UncontrolledPopover
                  placement="bottom"
                  target={`btn_tooltip_${o.id}`}
                  trigger="legacy"
                  className="popover-gantt"
                >
                  <PopoverBody>
                    <Container className="dashboard">
                      <Row>
                        <Col sm={12}>
                          <h5>Attached Documents</h5>
                        </Col>
                      </Row>
                      {operationActionPlanDocuments
                        .filter((b) => o.evidenceAttachmentIds.includes(b.id.toString()))
                        .map((d) => (
                          <Row>
                            <Col sm={9}>{d.documentName}</Col>
                            <Col sm={3} style={{ display: 'inline-flex' }}>
                              {d.documentLink && (
                                <LinkButton
                                  onClick={() => window.open(d.documentLink)}
                                  icon="file-download"
                                  text=""
                                />
                              )}
                            </Col>
                          </Row>
                        ))}
                    </Container>
                  </PopoverBody>
                </UncontrolledPopover>
              </>
            ))}
        </div>
      ),
    },
    {
      name: 'milestoneDescription',
      column: t('milestone'),
      value: (x) => truncateString(x.milestoneDescription, 25, 25),
    },
    {
      name: 'milestoneDate',
      column: t('dueDate'),
      value: (x) => customDateStr(x.milestoneDate),
    },
    {
      name: 'responsible',
      column: t('responsible'),
      value: (x) => truncateString(x.responsible, 25, 25),
    },
    {
      name: 'status',
      column: t('status'),
      value: (x) => x.status,
      render: (x, o) => (
        <ActionPlanStatusBadge
          code={x}
          locale={getIndicatorLocalization(
            o,
            operationActionPlan ? operationActionPlan.language : 'en',
            'status'
          )}
        />
      ),
    },
    {
      column: t('actions'),
      render: (x, o) => (
        <>
          <Button
            key={`tv_btn_edit_${o.id}`}
            className="btn-tertiary no-padding"
            htmlId={`tv_btn_edit_${o.id}`}
            icon={!readOnly ? 'edit' : 'eye'}
            text={!readOnly ? t('edit') : t('view')}
            onClick={() => {
              setSelectedItem(o);
              setShowNewActionPlanDetailModal(true);
            }}
          />
          {!readOnly && (
            <Button
              key={`tv_btn_download_${o.id}`}
              className="btn-tertiary no-padding"
              htmlId={`tv_btn_download_${o.id}`}
              icon="trash-alt"
              text=""
              onClick={() => {
                setSelectedItem(o);
                setShowDeleteActionRowModal(true);
              }}
            />
          )}
        </>
      ),
      css: 'S1',
    },
  ];

  const closeForm = () => {
    setShowNewActionPlanDetailModal(false);
    notify(t('saveActionPlanItemForm'), t('successSave'), 'success');
    reload();
    setServerError('');
  };

  const handleSubmit = (
    values: NewActionPlanDetailFormData,
    resetFormCallback: () => void,
    fileUpload: FormData | null
  ) => {
    const [policy, directive] = values.applicablePolicyOrDirective
      ? values.applicablePolicyOrDirective.split('|')
      : ['', ''];

    const [operationActionPlanDocumentId, flowDocumentId] = values.evidenceAttachment
      ? values.evidenceAttachment.split('|')
      : ['', ''];

    if (operationActionPlan) {
      const payload: OperationActionPlanItemRequestDto = {
        dateActionRecorded: values.dateActionRecorded as Date,
        compliancePolicyId: Number(policy),
        complianceDirectiveId: directive ? Number(directive) : null,
        topic: values.topic as string,
        action: values.action as string,
        evidence: values.evidence as string,
        flowDocumentId: flowDocumentId ? Number(flowDocumentId) : null,
        operationActionPlanDocumentId: operationActionPlanDocumentId
          ? Number(operationActionPlanDocumentId)
          : null,
        externalDocumentLink: values.externalDocumentLink,
        milestoneDescription: values.milestoneDescription ? values.milestoneDescription : '',
        milestoneDate: values.milestoneDate,
        responsible: values.responsible as string,
        status: values.status as string,
        priority: values.priority as string,
        operationActionPlanId: operationActionPlan.id,
        evidenceAttachmentIds: values.evidenceAttachmentIds ? values.evidenceAttachmentIds : null,
        externalDocumentLinkIds: values.externalDocumentLinkIds
          ? values.externalDocumentLinkIds
          : null,
        id: values.id,
      };
      setServerError('');
      if (fileUpload) {
        service(() => operActionPlanService.uploadDocument(payload, fileUpload), {
          callback: () => {
            closeForm();
          },
          loader: setSaveLoading,
          onError: (err) => {
            const msg = getErrorMessage(err);
            if (!msg.reportLink) {
              setServerError(msg.message);
            }
            return !!msg.reportLink;
          },
        });
      } else {
        service(() => operActionPlanService.saveActionPlanItem(payload), {
          callback: () => {
            closeForm();
          },
          loader: setSaveLoading,
          onError: (err) => {
            const msg = getErrorMessage(err);
            if (!msg.reportLink) {
              setServerError(msg.message);
            }
            return !!msg.reportLink;
          },
        });
      }
    }
  };

  const getFormState = (): NewActionPlanDetailFormData | undefined => {
    if (selectedItem) {
      return {
        id: selectedItem.id,
        dateActionRecorded: new Date(selectedItem.dateActionRecorded),
        applicablePolicyOrDirective: `${selectedItem.compliancePolicyId}|${
          selectedItem.complianceDirectiveId || ''
        }`,
        topic: selectedItem.topic,
        action: selectedItem.action,
        evidence: selectedItem.evidence,
        evidenceAttachment: `${selectedItem.operationActionPlanDocumentId || ''}|${
          selectedItem.flowDocumentId || ''
        }`,
        externalDocumentLink: selectedItem.externalDocumentLink,
        milestoneDescription: selectedItem.milestoneDescription
          ? selectedItem.milestoneDescription
          : null,
        milestoneDate: selectedItem.milestoneDate ? new Date(selectedItem.milestoneDate) : null,
        responsible: selectedItem.responsible,
        status: selectedItem.status,
        priority: selectedItem.priority,
        evidenceAttachmentIds: selectedItem.evidenceAttachmentIds,
        externalDocumentLinkIds: selectedItem.externalDocumentLinkIds,
      };
    }
    return undefined;
  };
  return (
    <>
      <SpinnerArea loading={saveLoading || loading}>
        <Container className="dashboard">
          <PageHeader title={t('actionPlanTitle', { number: operation.number })} />
          <Row>
            <Section>
              <div className="card__title d-flex justify-content-between">
                <h5>
                  {t('actionPlanV', {
                    version: operationActionPlan ? operationActionPlan.version : '0',
                  })}
                </h5>
                {!readOnly && (
                  <div>
                    <Button
                      type="button"
                      key="btnNewEmptyRow"
                      className="btn btn-secondary px-4 py-1"
                      text={t('newEmptyRow')}
                      onClick={() => {
                        setSelectedItem(null);
                        setShowNewActionPlanDetailModal(true);
                      }}
                    />
                  </div>
                )}
              </div>
              {operationActionPlanItems && (
                <Table
                  data={operationActionPlanItems.sort((a, b) => {
                    const statusOrder = ['1Open', '3Delayed', '2Closed', '4Canceled'];
                    let result = statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);

                    if (result === 0) {
                      result = a.priority.localeCompare(b.priority);
                    }
                    if (result === 0) {
                      result = sortDate(
                        new Date(a.dateActionRecorded),
                        new Date(b.dateActionRecorded)
                      );
                    }
                    return result;
                  })}
                  cells={cells}
                  keyFunc={(x) => x.id}
                  discapitalize
                  emptyNoDesc
                />
              )}

              {blokedBySR && (
                <div className="notification-wrapper-100 d-flex justify-content-center mb-3">
                  <BasicNotification
                    message={t('actionPlanStepWarningMessage', { step: stepSR })}
                    color="warning"
                    icon="exclamation-triangle"
                  />
                </div>
              )}
            </Section>
          </Row>
        </Container>
      </SpinnerArea>
      <CustomModal
        title={t('newActionPlanDetailTitle')}
        visible={showNewActionPlanDetailModal}
        setVisible={setShowNewActionPlanDetailModal}
        customClass="modal-dialog--header-task"
      >
        <NewActionPlanDetailForm
          handleSubmit={handleSubmit}
          handleCancel={() => {
            setServerError('');
            setShowNewActionPlanDetailModal(false);
          }}
          formState={getFormState()}
          language={operationActionPlan ? operationActionPlan.language : 'en'}
          readOnly={readOnly}
          policyDirectiveText={selectedItem ? selectedItem.policyDirectiveText : undefined}
          serverError={serverError}
        />
      </CustomModal>
      {selectedItem && operationActionPlan && (
        <ConfirmModal
          title={t('Delete Action Row')}
          buttonText={t('Delete')}
          visible={showDeleteActionRowModal}
          setVisible={setShowDeleteActionRowModal}
          action={() => {
            service(
              () =>
                operActionPlanService.deleteActionPlanItem(
                  Number(operationActionPlan.id),
                  selectedItem.id
                ),
              {
                loader: setSaveLoading,
                callback: () => {
                  setShowDeleteActionRowModal(false);
                  notify(t('Deleted successfully'), t('successSave'), 'success');
                  reload();
                },
              }
            );
          }}
        >
          {t('Are you sure you want to delete this Row? This action can not be undone.')}
        </ConfirmModal>
      )}
      )
    </>
  );
};

export default ActionPlanItem;
